import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle, X, Send, Minimize2, User, Mail, ArrowRight, RefreshCw,
  Headphones, Star, ThumbsUp, ThumbsDown, Download, Volume2, VolumeX,
  Sparkles, Clock, HelpCircle, Briefcase, Users, ChevronDown,
  SmilePlus, Phone, Shield, Zap, Heart, Bot,
  CheckCheck, ArrowLeft, Globe, BookOpen, ExternalLink, ChevronRight,
  MailPlus, Search, Paperclip, Sun, Sunrise, Sunset, Moon, PartyPopper, Bell
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_TOPICS = [
  { id: 'jobs', label: 'Job Opportunities', icon: Briefcase, color: '#8B5CF6' },
  { id: 'groups', label: 'Our Groups', icon: Globe, color: '#3B82F6' },
  { id: 'support', label: 'General Support', icon: HelpCircle, color: '#10B981' },
  { id: 'faq', label: 'Browse FAQ', icon: BookOpen, color: '#F59E0B' },
  { id: 'human', label: 'Talk to Someone', icon: Headphones, color: '#EF4444' },
];

const EMOJI_LIST = ['👍', '👋', '😊', '😂', '❤️', '🎉', '👀', '🙏', '💯', '🔥', '✨', '💬'];

const QUICK_LINKS = [
  { label: 'Vacancies', path: '/vacancies', icon: Briefcase },
  { label: 'News', path: '/news', icon: BookOpen },
  { label: 'Discord', path: 'https://discord.gg/2PFMA3hKjW', icon: MessageCircle, external: true },
  { label: 'Our Team', path: '/team', icon: Users },
];

// Parse quick replies from Nova's response
const parseQuickReplies = (text) => {
  const match = text.match(/\[QUICK_REPLIES:\s*(\{.*?\})\]/s);
  if (match) {
    try {
      const data = JSON.parse(match[1]);
      const cleanText = text.replace(/\[QUICK_REPLIES:.*?\]/s, '').trim();
      return { text: cleanText, replies: data.replies || [] };
    } catch { return { text, replies: [] }; }
  }
  return { text, replies: [] };
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [onlineStaffCount, setOnlineStaffCount] = useState(0);
  const [onlineStaff, setOnlineStaff] = useState([]);
  const [chatStatus, setChatStatus] = useState('idle');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [humanRequested, setHumanRequested] = useState(false);
  const [isConnectedToHuman, setIsConnectedToHuman] = useState(false);
  const [staffTyping, setStaffTyping] = useState(false);
  const [staffTypingName, setStaffTypingName] = useState('');
  const [systemStatus, setSystemStatus] = useState({ is_open: true, message: null });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [pulseButton, setPulseButton] = useState(true);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const messagesScrollRef = useRef(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyState, setNotifyState] = useState('idle'); // idle | loading | done | error
  const [notifyError, setNotifyError] = useState('');
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [welcomeTab, setWelcomeTab] = useState('home');
  const [failedAvatars, setFailedAvatars] = useState({});
  const [faqData, setFaqData] = useState([]);
  const [showFaq, setShowFaq] = useState(false);
  const [faqSearch, setFaqSearch] = useState('');
  const [reactions, setReactions] = useState({});
  const [showEmailTranscript, setShowEmailTranscript] = useState(false);
  const [transcriptEmail, setTranscriptEmail] = useState('');
  const [waitEstimate, setWaitEstimate] = useState(null); // { queue_position, estimated_wait_minutes }
  const [assignedStaff, setAssignedStaff] = useState(null); // { display_name, role, avatar, claimed_at }
  const [uploadingFile, setUploadingFile] = useState(false);
  const [ratingComment, setRatingComment] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevMessageCount = useRef(0);
  const customerTypingTimeoutRef = useRef(null);
  const customerIsTypingRef = useRef(false);

  useEffect(() => {
    let sid = localStorage.getItem('chat_session_id');
    const storedUserInfo = localStorage.getItem('chat_user_info');
    const storedStatus = localStorage.getItem('chat_status');
    if (!sid) { sid = uuidv4(); localStorage.setItem('chat_session_id', sid); }
    setSessionId(sid);

    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        const accountName = [parsed.first_name, parsed.last_name].filter(Boolean).join(' ');
        const accountEmail = parsed.email;
        if (accountName && accountEmail) {
          const autoInfo = { name: accountName, email: accountEmail };
          setUserInfo(autoInfo);
          localStorage.setItem('chat_user_info', JSON.stringify(autoInfo));
        }
      } catch {}
    } else if (storedUserInfo) {
      setUserInfo(JSON.parse(storedUserInfo));
    }

    if (storedStatus) {
      setChatStatus(storedStatus);
      if (storedStatus === 'active' || storedStatus === 'connected') loadChatHistory(sid);
    }
    fetchOnlineStaff();
    fetchFaq();
    const presencePoll = setInterval(fetchOnlineStaff, 30000);
    const pulseTimer = setTimeout(() => setPulseButton(false), 5000);
    return () => { clearTimeout(pulseTimer); clearInterval(presencePoll); };
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (messages.length > prevMessageCount.current && prevMessageCount.current > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.sender_type !== 'user') {
        if (!isOpen && soundEnabled) setUnreadCount(prev => prev + 1);
      }
    }
    prevMessageCount.current = messages.length;
  }, [messages, isOpen, soundEnabled]);

  const fetchFaq = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/chat/faq`);
      setFaqData(r.data || []);
    } catch {}
  };

  const updateUserStatus = async (online) => {
    if (!sessionId) return;
    try { await axios.post(`${BACKEND_URL}/api/chat/user-status/${sessionId}?online=${online}`); } catch {}
  };

  const markMessagesAsRead = async () => {
    if (!sessionId) return;
    try { await axios.post(`${BACKEND_URL}/api/chat/mark-read/${sessionId}`); } catch {}
  };

  const checkTypingStatus = async () => {
    if (!sessionId) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/typing-status/${sessionId}`);
      setStaffTyping(response.data.staff_typing);
      setStaffTypingName(response.data.staff_name || 'Staff');
    } catch { setStaffTyping(false); }
  };

  const fetchOnlineStaff = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/online-staff`);
      setOnlineStaffCount(response.data.count);
      setOnlineStaff(response.data.staff || []);
    } catch {}
  };

  const fetchWaitEstimate = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/chat/wait-estimate`);
      setWaitEstimate(r.data);
    } catch {}
  };

  const fetchAssignedStaff = async () => {
    if (!sessionId) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/chat/assigned-staff/${sessionId}`);
      setAssignedStaff(r.data?.assigned ? r.data : null);
    } catch { setAssignedStaff(null); }
  };

  const sendSessionTopic = async (topic) => {
    if (!sessionId || !topic) return;
    try {
      await axios.post(`${BACKEND_URL}/api/chat/set-topic/${sessionId}`, { topic });
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File too large — max 8MB.');
      e.target.value = '';
      return;
    }
    setUploadingFile(true);
    try {
      const form = new FormData();
      form.append('session_id', sessionId);
      form.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/chat/upload-attachment`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Insert a chat message referencing the attachment
      const messageBody = r.data.is_image
        ? `📎 ${r.data.filename}\n${r.data.url}`
        : `📎 Attachment: ${r.data.filename}\n${r.data.url}`;
      await axios.post(`${BACKEND_URL}/api/chat/send`, {
        session_id: sessionId,
        message: messageBody,
        sender_type: 'user',
        sender_name: userInfo.name || 'Customer',
      });
      loadChatHistory(sessionId, false);
      toast.success('Attachment sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/system-status`);
      setSystemStatus(response.data);
    } catch {}
  };

  useEffect(() => {
    fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let interval;
    if (isOpen && sessionId && !isMinimized && (chatStatus === 'active' || chatStatus === 'connected')) {
      updateUserStatus(true);
      markMessagesAsRead();
      fetchAssignedStaff();
      if (humanRequested && !isConnectedToHuman) fetchWaitEstimate();
      interval = setInterval(() => {
        loadChatHistory(sessionId, false);
        checkTypingStatus();
        markMessagesAsRead();
        checkSessionStatus();
        fetchAssignedStaff();
        if (humanRequested && !isConnectedToHuman) fetchWaitEstimate();
      }, 5000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isOpen, sessionId, isMinimized, chatStatus, humanRequested, isConnectedToHuman]);

  const loadChatHistory = async (sid, fullReload = true) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/history/${sid}`, { timeout: 10000 });
      const serverMessages = response.data || [];
      const hasStaffResponse = serverMessages.some(m => m.sender_type === 'staff');
      if (hasStaffResponse && !isConnectedToHuman) {
        setIsConnectedToHuman(true);
        setChatStatus('connected');
        localStorage.setItem('chat_status', 'connected');
      }
      // Load reactions from messages
      const rxns = {};
      serverMessages.forEach(m => { if (m.reaction) rxns[m.message_id] = m.reaction; });
      setReactions(prev => ({ ...prev, ...rxns }));

      if (fullReload) {
        setMessages(serverMessages);
      } else {
        setMessages(prev => {
          if (serverMessages.length === 0 && prev.length > 0) return prev;
          const sPrev = JSON.stringify(prev.slice(-5).map(m => ({ id: m.message_id, msg: m.message, del: m.deleted, ed: m.edited })));
          const sServer = JSON.stringify(serverMessages.slice(-5).map(m => ({ id: m.message_id, msg: m.message, del: m.deleted, ed: m.edited })));
          if (sPrev !== sServer || serverMessages.length !== prev.length) return serverMessages;
          return prev;
        });
      }
    } catch {}
  };

  const checkSessionStatus = async () => {
    if (!sessionId) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/session-info/${sessionId}`, { timeout: 5000 });
      if (response.data.status === 'resolved' && chatStatus !== 'idle') {
        setShowRating(true);
      }
    } catch {}
  };

  const handleStartChat = () => {
    // When closed: still allow chatting with Nova AI (no human handoff). Open access to FAQs etc.
    const userData = localStorage.getItem('user_data');
    if (userData && userInfo.name && userInfo.email) {
      handleSubmitUserInfo();
      return;
    }
    setChatStatus('intro');
  };

  const handleSubmitUserInfo = async () => {
    if (!userInfo.name.trim() || !userInfo.email.trim()) return;
    localStorage.setItem('chat_user_info', JSON.stringify(userInfo));
    setTranscriptEmail(userInfo.email);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/register-user`, {
        session_id: sessionId, name: userInfo.name, email: userInfo.email
      });
      if (response.data.status === 'existing' && response.data.existing_session_id) {
        const existingSessionId = response.data.existing_session_id;
        localStorage.setItem('chat_session_id', existingSessionId);
        setSessionId(existingSessionId);
        localStorage.setItem('chat_status', 'active');
        setChatStatus('active');
        await loadChatHistory(existingSessionId, true);
        return;
      }
      localStorage.setItem('chat_status', 'active');
      setChatStatus('active');
      await loadChatHistory(sessionId, true);

      if (selectedTopic && selectedTopic !== 'faq') {
        // Persist topic on backend for staff routing
        sendSessionTopic(selectedTopic).catch(() => {});
        setTimeout(() => {
          const topicMessages = {
            jobs: "I'm interested in job opportunities at Starnight Holdings!",
            groups: "I'd like to learn more about your groups and establishments.",
            support: "Hi, I need some help with something.",
            human: "Could I speak with a member of your team please?"
          };
          setInputMessage(topicMessages[selectedTopic] || '');
          setSelectedTopic(null);
        }, 1500);
      }
    } catch {
      localStorage.setItem('chat_status', 'active');
      setChatStatus('active');
      setMessages([{
        message: `Hey ${userInfo.name}! I'm Nova, your virtual assistant at Starnight Holdings. What can I help you with?`,
        sender_type: 'bot', sender_name: 'Nova', timestamp: new Date().toISOString()
      }]);
    }
  };

  const handleSendMessage = async (overrideMessage) => {
    const messageText = (overrideMessage || inputMessage).trim();
    if (!messageText || isLoading) return;
    const userMessage = {
      message: messageText, sender_type: 'user', sender_name: userInfo.name || 'You',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setShowEmojiPicker(false);
    if (customerIsTypingRef.current) {
      customerIsTypingRef.current = false;
      if (customerTypingTimeoutRef.current) clearTimeout(customerTypingTimeoutRef.current);
      axios.delete(`${BACKEND_URL}/api/chat/customer-typing/${sessionId}`).catch(() => {});
    }

    const humanKeywords = ['human', 'agent', 'person', 'real person', 'speak to', 'talk to', 'connect', 'representative', 'staff', 'support team', 'live chat', 'live agent'];
    const isRequestingHuman = humanKeywords.some(kw => messageText.toLowerCase().includes(kw));

    // When live chat is closed, BLOCK human escalation entirely and keep the user in AI-only mode.
    if (isRequestingHuman && !systemStatus.is_open) {
      setIsLoading(false);
      setMessages(prev => [...prev, {
        message: `I'm sorry — our live chat team is offline right now and I can't connect you to a human at this moment.\n\nI'm Nova, our AI assistant, and I'm here 24/7 to help with whatever you need. If you'd still like a human reply, just email **help@starnightholdings.uk** and we'll get back to you as soon as our team is back online.`,
        sender_type: 'bot', sender_name: 'Nova', timestamp: new Date().toISOString()
      }]);
      // Still log the message to backend for context (no human routing)
      try {
        await axios.post(`${BACKEND_URL}/api/chat/send`, {
          session_id: sessionId, message: messageText, sender_type: 'user',
          sender_name: userInfo.name, requesting_human: false
        });
      } catch {}
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/send`, {
        session_id: sessionId, message: messageText, sender_type: 'user',
        sender_name: userInfo.name, requesting_human: (isRequestingHuman || humanRequested) && systemStatus.is_open
      });
      if (isRequestingHuman && !humanRequested && systemStatus.is_open) {
        setHumanRequested(true);
        setMessages(prev => [...prev, {
          message: `No problem, ${userInfo.name}! I'm connecting you to our support team now.\n\nA staff member will be with you shortly.${onlineStaffCount > 0 ? ` ${onlineStaffCount} agent${onlineStaffCount > 1 ? 's' : ''} currently available.` : ''}`,
          sender_type: 'bot', sender_name: 'Nova', timestamp: new Date().toISOString()
        }]);
      } else if (!humanRequested && !isConnectedToHuman && response.data.bot_response) {
        setMessages(prev => [...prev, {
          message: response.data.bot_response, sender_type: 'bot',
          sender_name: 'Nova', timestamp: new Date().toISOString(),
          message_id: response.data.message_id || `bot-${Date.now()}`
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        message: 'Sorry, I encountered an error. Please try again.',
        sender_type: 'bot', sender_name: 'System', timestamp: new Date().toISOString()
      }]);
    } finally { setIsLoading(false); }
  };

  const handleQuickReply = (reply) => {
    if (reply === 'View Vacancies') { window.open('/vacancies', '_blank'); return; }
    if (reply === 'Join Discord') { window.open('https://discord.gg/2PFMA3hKjW', '_blank'); return; }
    if (reply === 'Explore Groups') { window.open('/#groups', '_blank'); return; }
    if (reply === 'Browse FAQ') { setShowFaq(true); return; }
    if (reply === 'Email Us') { window.open('mailto:help@starnightholdings.uk', '_blank'); return; }
    if (reply === 'Talk to Human') { handleSendMessage('Could I speak with a member of your team please?'); return; }
    handleSendMessage(reply);
  };

  const handleReaction = async (messageId, reaction) => {
    const current = reactions[messageId];
    const newReaction = current === reaction ? null : reaction;
    setReactions(prev => ({ ...prev, [messageId]: newReaction }));
    if (newReaction) {
      try {
        await axios.post(`${BACKEND_URL}/api/chat/react/${messageId}`, { reaction: newReaction });
        if (newReaction === 'heart') toast.success('❤️ Thanks passed along', { duration: 1800 });
      } catch {}
    }
  };

  const handleEmailTranscript = async () => {
    if (!transcriptEmail.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/chat/email-transcript`, {
        session_id: sessionId, email: transcriptEmail
      });
      toast.success('Transcript saved! Email delivery coming soon.');
      setShowEmailTranscript(false);
    } catch {
      toast.error('Failed to save transcript.');
    }
  };

  const handleNewChat = () => {
    const newSid = uuidv4();
    localStorage.setItem('chat_session_id', newSid);
    localStorage.removeItem('chat_user_info');
    localStorage.removeItem('chat_status');
    setSessionId(newSid);
    setUserInfo({ name: '', email: '' });
    setChatStatus('idle');
    setMessages([]);
    setHumanRequested(false);
    setIsConnectedToHuman(false);
    setShowRating(false);
    setRating(0);
    setRatingSubmitted(false);
    setSelectedTopic(null);
    setShowFaq(false);
    setReactions({});
    setWaitEstimate(null);
    setAssignedStaff(null);
    setRatingComment('');
  };

  const handleEndChat = () => { setShowRating(true); };

  const handleSubmitRating = async () => {
    try {
      // Submit via real ratings API (with comment)
      await axios.post(`${BACKEND_URL}/api/chat/rating/${sessionId}`, {
        stars: rating,
        comment: ratingComment || '',
      });
    } catch {
      // fallback: log via send if rating endpoint fails
      try {
        await axios.post(`${BACKEND_URL}/api/chat/send`, {
          session_id: sessionId, message: `[Rating: ${rating}/5 stars${ratingComment ? ' — ' + ratingComment : ''}]`,
          sender_type: 'system', sender_name: 'System'
        });
      } catch {}
    }
    setRatingSubmitted(true);
    setTimeout(() => handleNewChat(), 2200);
  };

  const handleDownloadTranscript = () => {
    const transcript = messages.map(m => {
      const time = m.timestamp ? new Date(m.timestamp).toLocaleString() : '';
      const sender = m.sender_type === 'user' ? (userInfo.name || 'You') : (m.sender_name || 'Bot');
      return `[${time}] ${sender}: ${m.message}`;
    }).join('\n\n');
    const blob = new Blob([`Chat Transcript - Starnight Holdings\nDate: ${new Date().toLocaleDateString()}\n${'='.repeat(50)}\n\n${transcript}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `starnight-chat-${new Date().toISOString().split('T')[0]}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (chatStatus === 'intro') handleSubmitUserInfo();
      else handleSendMessage();
    }
  };

  const handleCustomerInputChange = (e) => {
    setInputMessage(e.target.value);
    if (!sessionId || (chatStatus !== 'active' && chatStatus !== 'connected')) return;
    if (!customerIsTypingRef.current) {
      customerIsTypingRef.current = true;
      axios.post(`${BACKEND_URL}/api/chat/customer-typing/${sessionId}`).catch(() => {});
    }
    if (customerTypingTimeoutRef.current) clearTimeout(customerTypingTimeoutRef.current);
    customerTypingTimeoutRef.current = setTimeout(() => {
      customerIsTypingRef.current = false;
      axios.delete(`${BACKEND_URL}/api/chat/customer-typing/${sessionId}`).catch(() => {});
    }, 3000);
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const diff = Date.now() - new Date(timestamp).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredFaq = faqData.filter(f =>
    f.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
    f.answer.toLowerCase().includes(faqSearch.toLowerCase())
  );

  // Floating button
  if (!isOpen) {
    const _h = new Date().getHours();
    const _greet = _h < 5 ? { label: 'Working late', Icon: Moon }
                  : _h < 12 ? { label: 'Good morning', Icon: Sunrise }
                  : _h < 17 ? { label: 'Good afternoon', Icon: Sun }
                  : _h < 21 ? { label: 'Good evening', Icon: Sunset }
                              : { label: 'Good evening', Icon: Moon };
    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        <style>{`
          @keyframes launcher-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
          @keyframes launcher-shine { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          @keyframes launcher-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        `}</style>
        {unreadCount > 0 && (
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-xl px-3.5 py-2 ring-1 ring-cyan-500/30 shadow-2xl backdrop-blur-xl" style={{ animation: 'slideUp 0.3s ease-out' }}>
            <span aria-hidden className="absolute inset-0 opacity-40 bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" style={{ backgroundSize: '200% 100%', animation: 'launcher-shine 2.5s linear infinite' }} />
            <p className="relative text-white text-[12px] font-semibold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_6px_rgba(34,211,238,0.9)]" />
              {unreadCount} new message{unreadCount > 1 ? 's' : ''}
            </p>
          </div>
        )}

        <div className="relative group">
          {/* Ambient glow */}
          <div aria-hidden className={`absolute -inset-3 rounded-full blur-2xl pointer-events-none transition-opacity ${systemStatus.is_open ? 'bg-cyan-500/25' : 'bg-slate-500/20'} opacity-70 group-hover:opacity-100`} />

          {/* Conic halo ring */}
          <div
            aria-hidden
            className="absolute -inset-[3px] rounded-[22px] opacity-80 pointer-events-none"
            style={{
              background: systemStatus.is_open
                ? 'conic-gradient(from 0deg, transparent 0deg, #00E5FF 90deg, transparent 180deg, #8B5CF6 270deg, transparent 360deg)'
                : 'conic-gradient(from 0deg, transparent 0deg, #64748B 90deg, transparent 180deg, #475569 270deg, transparent 360deg)',
              animation: 'launcher-spin 6s linear infinite',
              maskImage: 'radial-gradient(closest-side, transparent 62%, black 64%, black 100%)',
              WebkitMaskImage: 'radial-gradient(closest-side, transparent 62%, black 64%, black 100%)',
            }}
          />

          {/* Hover tooltip label */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
            <div className="whitespace-nowrap px-3.5 py-2 rounded-full bg-slate-950/95 ring-1 ring-cyan-500/25 shadow-xl backdrop-blur-md">
              <p className="text-white text-[11px] font-semibold tracking-wide leading-none">
                {systemStatus.is_open ? 'Chat with our team' : 'Ask Nova AI 24/7'}
              </p>
              <p className="mt-1 text-[9px] text-cyan-300/70 tracking-wider uppercase leading-none flex items-center gap-1">
                <_greet.Icon className="w-2.5 h-2.5" /> {_greet.label}
              </p>
            </div>
          </div>

          <button
            onClick={() => { setIsOpen(true); setUnreadCount(0); }}
            data-testid="chat-widget-button"
            className={`relative w-16 h-16 rounded-2xl p-[2px] shadow-2xl transition-transform duration-300 hover:scale-105 active:scale-95 ${systemStatus.is_open ? 'bg-gradient-to-br from-cyan-400 via-blue-500 to-cyan-600 shadow-cyan-500/40' : 'bg-gradient-to-br from-zinc-600 to-zinc-800 shadow-zinc-800/60'}`}
          >
            <div className="relative w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center overflow-hidden">
              <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%]" style={{ animation: 'launcher-shine 2s linear infinite' }} />
              <MessageCircle className="relative w-6 h-6 text-white drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]" strokeWidth={2.2} />
            </div>

            {/* Online status crest */}
            <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ring-[2.5px] ring-slate-950 flex items-center justify-center shadow-lg ${systemStatus.is_open && onlineStaffCount > 0 ? 'bg-emerald-400 shadow-emerald-500/40' : !systemStatus.is_open ? 'bg-rose-500 shadow-rose-500/40' : 'bg-amber-400 shadow-amber-500/40'}`}>
              <span className={`absolute inset-0 rounded-full animate-ping opacity-60 ${systemStatus.is_open && onlineStaffCount > 0 ? 'bg-emerald-400' : !systemStatus.is_open ? 'bg-rose-500' : 'bg-amber-400'}`} />
            </span>

            {/* Unread badge */}
            {unreadCount > 0 && (
              <span className="absolute -top-2 -left-2 min-w-[22px] h-[22px] px-1.5 bg-gradient-to-br from-rose-500 to-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-rose-500/40 ring-2 ring-slate-950 animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}

            {pulseButton && systemStatus.is_open && (
              <span className="absolute inset-0 rounded-2xl bg-cyan-400/25 animate-ping pointer-events-none" />
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="chat-widget-container"
      className={`fixed bottom-6 right-6 z-50 bg-[#0A0A12] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 transition-all duration-300 overflow-hidden ${
        isMinimized ? 'w-80 h-16' : 'w-[400px] h-[600px]'
      }`}
      style={{ backdropFilter: 'blur(20px)' }}
    >
      {/* Header */}
      <div className="relative bg-gradient-to-r from-[#0D1117] to-[#0F1520] p-4 flex items-center justify-between border-b border-white/[0.06]">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent" />
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00E5FF]/20 to-[#00E5FF]/5 rounded-xl flex items-center justify-center border border-[#00E5FF]/20">
              {isConnectedToHuman ? <Headphones className="w-5 h-5 text-emerald-400" /> : <Bot className="w-5 h-5 text-[#00E5FF]" />}
            </div>
            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0D1117] ${
              isConnectedToHuman ? 'bg-emerald-400' : onlineStaffCount > 0 ? 'bg-emerald-400' : 'bg-zinc-500'
            }`} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm tracking-tight" data-testid="chat-header-title">Starnight Support</h3>
            <p className="text-white/40 text-[11px] flex items-center gap-1.5">
              {isConnectedToHuman ? (
                <><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />Connected to agent</>
              ) : humanRequested ? (
                <><span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />Waiting for agent...</>
              ) : !systemStatus.is_open ? (
                <><span className="w-1.5 h-1.5 bg-amber-400 rounded-full" />AI-only · Team offline</>
              ) : (
                <><span className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full" />{onlineStaffCount > 0 ? `${onlineStaffCount} online` : 'AI Assistant'}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 relative">
          <button onClick={() => setSoundEnabled(!soundEnabled)} data-testid="toggle-sound-btn"
            className="text-white/30 hover:text-cyan-300 hover:bg-cyan-500/10 p-2 rounded-lg transition-all" title={soundEnabled ? 'Mute' : 'Unmute'}>
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          {(chatStatus === 'active' || chatStatus === 'connected') && (
            <div className="relative">
              <button onClick={() => setHeaderMenuOpen(v => !v)} data-testid="header-menu-btn"
                className={`p-2 rounded-lg transition-all ${headerMenuOpen ? 'bg-cyan-500/15 text-cyan-300' : 'text-white/30 hover:text-cyan-300 hover:bg-cyan-500/10'}`}
                title="More actions">
                <span className="block w-4 h-4 relative">
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-current" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-current" />
                  <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-current" />
                </span>
              </button>
              {headerMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setHeaderMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1.5 w-48 z-50 rounded-xl bg-slate-950/95 ring-1 ring-cyan-500/20 shadow-2xl backdrop-blur-md overflow-hidden" style={{ animation: 'fadeInUp 0.15s ease-out' }} data-testid="header-menu">
                    <button onClick={() => { setShowEmailTranscript(!showEmailTranscript); setHeaderMenuOpen(false); }} data-testid="email-transcript-btn"
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-[12px] font-medium text-white/70 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all">
                      <MailPlus className="w-3.5 h-3.5" /> Email transcript
                    </button>
                    <button onClick={() => { handleDownloadTranscript(); setHeaderMenuOpen(false); }} data-testid="download-transcript-btn"
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-[12px] font-medium text-white/70 hover:text-cyan-300 hover:bg-cyan-500/10 transition-all">
                      <Download className="w-3.5 h-3.5" /> Download chat
                    </button>
                    <div className="h-px bg-white/[0.06]" />
                    <button onClick={() => { handleNewChat(); setHeaderMenuOpen(false); }} data-testid="new-chat-btn"
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-[12px] font-medium text-rose-300/80 hover:text-rose-300 hover:bg-rose-500/10 transition-all">
                      <RefreshCw className="w-3.5 h-3.5" /> Start new chat
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <button onClick={() => setIsMinimized(!isMinimized)}
            className="text-white/30 hover:text-cyan-300 hover:bg-cyan-500/10 p-2 rounded-lg transition-all" title={isMinimized ? 'Expand' : 'Minimize'}>
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={() => { updateUserStatus(false); setIsOpen(false); }}
            className="text-white/30 hover:text-rose-300 hover:bg-rose-500/10 p-2 rounded-lg transition-all" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Email Transcript Bar — Vault */}
          {showEmailTranscript && (
            <div className="relative bg-gradient-to-b from-cyan-500/[0.05] to-transparent border-b border-cyan-500/15 px-3 py-2.5 flex items-center gap-2" data-testid="email-transcript-bar" style={{ animation: 'fadeInUp 0.2s ease-out' }}>
              <Mail className="w-3.5 h-3.5 text-cyan-400/70 flex-shrink-0" />
              <input type="email" value={transcriptEmail} onChange={e => setTranscriptEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-slate-950/60 ring-1 ring-white/[0.08] focus:ring-cyan-500/40 rounded-lg px-3 py-1.5 text-white text-[11.5px] placeholder-white/25 focus:outline-none transition-all" />
              <button onClick={handleEmailTranscript}
                className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 ring-1 ring-cyan-500/30 hover:ring-cyan-500/50 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all">
                Send
              </button>
              <button onClick={() => setShowEmailTranscript(false)}
                className="text-white/30 hover:text-rose-300 hover:bg-rose-500/10 p-1.5 rounded-lg transition-all">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Welcome Screen */}
          {chatStatus === 'idle' && !showFaq && (
            <div className="h-[536px] flex flex-col overflow-y-auto no-scrollbar relative" data-testid="chat-welcome-screen">
              <style>{`
                @keyframes welcome-shine { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
                @keyframes welcome-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
              `}</style>
              {/* Ambient Vault backdrop */}
              <div aria-hidden className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-24 -left-12 w-72 h-72 rounded-full blur-3xl bg-cyan-500/10" />
                <div className="absolute -bottom-32 -right-10 w-80 h-80 rounded-full blur-3xl bg-violet-600/8" />
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)', backgroundSize: '22px 22px' }} />
              </div>
              {true ? (
                (() => {
                  const _h = new Date().getHours();
                  const _greet = _h < 5 ? { label: 'Working late · we are here', Icon: Moon }
                                : _h < 12 ? { label: 'Good morning', Icon: Sunrise }
                                : _h < 17 ? { label: 'Good afternoon', Icon: Sun }
                                : _h < 21 ? { label: 'Good evening', Icon: Sunset }
                                            : { label: 'Good evening', Icon: Moon };
                  const GreetIcon = _greet.Icon;
                  const welcomeTabs = [
                    { id: 'home', label: 'Home', icon: Sparkles },
                    { id: 'chat', label: 'Chat',  icon: MessageCircle },
                    { id: 'faq',  label: 'FAQ',   icon: BookOpen },
                    { id: 'info', label: 'Info',  icon: Users },
                  ];
                  const CHAT_STARTERS = [
                    { id: 'talk',      label: 'Talk to somebody',   sub: 'Get a real team member',   icon: Headphones, color: '#00E5FF', prompt: "I'd like to talk to somebody from the team, please." },
                    { id: 'jobs',      label: 'Careers & vacancies',sub: 'How to apply, current roles', icon: Briefcase, color: '#8B5CF6', prompt: "I'm interested in careers — what roles are open?" },
                    { id: 'buy',       label: 'Buy or refund',      sub: 'Billing, purchases, refunds', icon: Zap,        color: '#F59E0B', prompt: "I have a question about a purchase or refund." },
                    { id: 'complaint', label: 'Raise a concern',    sub: 'Complaints & feedback',    icon: Shield,     color: '#F43F5E', prompt: "I need to raise a complaint or feedback." },
                    { id: 'groups',    label: 'Join our groups',    sub: 'Roblox venues & Discord',  icon: Users,      color: '#EC4899', prompt: "How do I join your Roblox groups?" },
                    { id: 'general',   label: 'Something else',     sub: 'Ask anything',             icon: MessageCircle, color: '#10B981', prompt: "Hi! I have a question." },
                  ];
                  const startWith = (starter) => {
                    setSelectedTopic(starter.id);
                    setInputMessage(starter.prompt);
                    handleStartChat();
                  };
                  return (
                    <div className="relative flex-1 flex flex-col p-5 overflow-y-auto no-scrollbar">
                      {/* Vault header */}
                      <div className="text-center mb-4">
                        <div className="relative w-14 h-14 mx-auto mb-3">
                          <div aria-hidden className="absolute -inset-1 rounded-2xl opacity-60 blur-[2px]" style={{ background: 'conic-gradient(from 0deg, transparent 0deg, #00E5FF 90deg, transparent 180deg, #8B5CF6 270deg, transparent 360deg)', animation: 'welcome-spin 6s linear infinite', maskImage: 'radial-gradient(closest-side, transparent 68%, black 70%, black 100%)', WebkitMaskImage: 'radial-gradient(closest-side, transparent 68%, black 70%, black 100%)' }} />
                          <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 p-[2px] shadow-xl shadow-cyan-500/30">
                            <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center">
                              <Sparkles className="w-6 h-6 text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.6)]" strokeWidth={2.2} />
                            </div>
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-4.5 h-4.5 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center ring-[2px] ring-[#0A0A12] shadow-lg shadow-emerald-500/40" style={{ width: '18px', height: '18px' }}>
                            <Bot className="w-2 h-2 text-white" strokeWidth={2.5} />
                          </div>
                        </div>
                        <p className="text-[9.5px] font-bold uppercase tracking-[0.24em] text-cyan-400/80 flex items-center gap-1.5 justify-center mb-1">
                          <GreetIcon className="w-3 h-3" />
                          {_greet.label}
                        </p>
                        <h2 className="text-white text-lg font-bold tracking-tight">How can we help?</h2>
                      </div>

                      {/* Live agent avatar row */}
                      {onlineStaff.length > 0 && (
                        <div className="mb-3 flex items-center justify-center gap-2" data-testid="online-agents-row">
                          <div className="flex items-center -space-x-2">
                            {onlineStaff.slice(0, 4).map((s) => {
                              const avatarUrl = s.avatar && s.avatar.startsWith('http') ? s.avatar : s.avatar ? `${BACKEND_URL}${s.avatar.startsWith('/api') ? s.avatar : '/api' + s.avatar}` : null;
                              const failed = failedAvatars[`online-${s.username}`];
                              return (
                                <div key={s.username} className="relative group" title={`${s.display_name} · ${s.role}`}>
                                  <div className="w-7 h-7 rounded-full bg-slate-900 ring-2 ring-[#0A0A12] overflow-hidden flex items-center justify-center transition-transform group-hover:scale-110 group-hover:z-10">
                                    {avatarUrl && !failed ? (
                                      <img src={avatarUrl} alt={s.display_name} className="w-full h-full object-cover" onError={() => setFailedAvatars(prev => ({ ...prev, [`online-${s.username}`]: true }))} />
                                    ) : (
                                      <span className="text-[9px] font-bold text-cyan-300">{s.initials || '??'}</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/25">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.9)]" />
                            <span className="text-emerald-300 text-[10px] font-semibold tracking-wide">{onlineStaff.length} online</span>
                          </div>
                        </div>
                      )}

                      {/* Tab bar */}
                      <div className="mb-4 flex items-center gap-1 p-1 rounded-xl bg-slate-950/50 ring-1 ring-white/[0.05]" data-testid="welcome-tabs">
                        {welcomeTabs.map(t => {
                          const active = welcomeTab === t.id;
                          return (
                            <button
                              key={t.id}
                              onClick={() => setWelcomeTab(t.id)}
                              data-testid={`welcome-tab-${t.id}`}
                              className={`relative flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                                active ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/10 text-cyan-300 ring-1 ring-cyan-500/30 shadow-sm shadow-cyan-500/20' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.03]'
                              }`}
                            >
                              <t.icon className={`w-3.5 h-3.5 ${active ? 'text-cyan-400' : ''}`} />
                              {t.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* HOME TAB */}
                      {welcomeTab === 'home' && (
                        <div className="flex-1 flex flex-col" data-testid="welcome-home-panel">
                          {/* Live status card — state-aware */}
                          {systemStatus.is_open ? (
                            <div className="mb-3 rounded-xl p-3 bg-gradient-to-br from-emerald-500/[0.08] to-cyan-500/[0.04] ring-1 ring-emerald-500/20">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="relative flex w-2 h-2">
                                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                                  <span className="relative rounded-full w-2 h-2 bg-emerald-400" />
                                </span>
                                <p className="text-emerald-300 text-[11px] font-bold uppercase tracking-[0.14em]">We're live</p>
                                <span className="ml-auto text-white/50 text-[10px] font-semibold">~5 min replies</span>
                              </div>
                              <p className="text-white/60 text-[11.5px] leading-relaxed">
                                {onlineStaff.length > 0 ? `${onlineStaff.length} team member${onlineStaff.length > 1 ? 's are' : ' is'} on the desk` : 'Nova AI is on the desk'} · Ask anything.
                              </p>
                            </div>
                          ) : (
                            <div className="mb-3 rounded-xl p-3 bg-gradient-to-br from-slate-800/60 to-slate-900/40 ring-1 ring-white/[0.08]">
                              <div className="flex items-center gap-2 mb-1">
                                <Moon className="w-3 h-3 text-cyan-300/70" />
                                <p className="text-white/70 text-[11px] font-bold uppercase tracking-[0.14em]">Team off-shift</p>
                                <span className="ml-auto text-cyan-300/70 text-[10px] font-semibold">Nova 24/7</span>
                              </div>
                              <p className="text-white/50 text-[11.5px] leading-relaxed">
                                {systemStatus.message || "Live chat resumes 10 AM UK. Nova AI is here in the meantime."}
                              </p>
                            </div>
                          )}

                          {/* Notify-me toggle (offline only) */}
                          {!systemStatus.is_open && notifyState !== 'done' && (
                            !showNotifyForm ? (
                              <button
                                onClick={() => setShowNotifyForm(true)}
                                data-testid="notify-back-toggle"
                                className="group mb-3 w-full p-2.5 rounded-xl ring-1 ring-cyan-500/20 bg-cyan-500/[0.04] hover:ring-cyan-500/40 hover:bg-cyan-500/[0.08] transition-all text-left"
                              >
                                <div className="flex items-center gap-2.5">
                                  <Bell className="w-3.5 h-3.5 text-cyan-300 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white text-[11.5px] font-semibold leading-tight">Notify me when we're back</p>
                                    <p className="text-white/40 text-[10px] mt-0.5 leading-tight">One-tap email ping · no spam</p>
                                  </div>
                                  <ChevronRight className="w-3.5 h-3.5 text-cyan-300/60 group-hover:translate-x-0.5 transition-transform" />
                                </div>
                              </button>
                            ) : (
                              <form onSubmit={async (e) => {
                                e.preventDefault();
                                if (notifyState === 'loading') return;
                                setNotifyState('loading'); setNotifyError('');
                                try {
                                  await axios.post(`${BACKEND_URL}/api/chat/notify-when-back`, { email: notifyEmail, message: notifyMessage, session_id: sessionId });
                                  setNotifyState('done');
                                } catch (err) {
                                  setNotifyError(err?.response?.data?.detail || 'Something went wrong');
                                  setNotifyState('error');
                                }
                              }} className="mb-3 rounded-xl p-3 ring-1 ring-cyan-500/25 bg-cyan-500/[0.04]" data-testid="notify-back-form">
                                <div className="flex items-center gap-2 mb-2">
                                  <Bell className="w-3.5 h-3.5 text-cyan-300" />
                                  <p className="text-cyan-300 text-[11px] font-bold uppercase tracking-[0.12em]">Ping me when live</p>
                                  <button type="button" onClick={() => { setShowNotifyForm(false); setNotifyError(''); }} className="ml-auto text-white/30 hover:text-white/70 p-0.5">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                                <input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                                  placeholder="your@email.com" autoFocus required data-testid="notify-back-email"
                                  className="w-full bg-slate-950/60 ring-1 ring-white/[0.08] focus:ring-cyan-500/40 rounded-lg px-3 py-2 text-white text-[12px] placeholder-white/25 focus:outline-none transition-all mb-2" />
                                {notifyError && (
                                  <p className="text-rose-300 text-[10.5px] font-medium mb-2">{notifyError}</p>
                                )}
                                <button type="submit" disabled={notifyState === 'loading' || !notifyEmail.trim()} data-testid="notify-back-submit"
                                  className="w-full py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-[12px] font-semibold shadow-lg shadow-cyan-500/25 disabled:opacity-40 transition-all flex items-center justify-center gap-1.5">
                                  {notifyState === 'loading' ? 'Adding you…' : <><Bell className="w-3.5 h-3.5" /> Ping me back</>}
                                </button>
                              </form>
                            )
                          )}
                          {!systemStatus.is_open && notifyState === 'done' && (
                            <div className="mb-3 rounded-xl p-2.5 bg-emerald-500/[0.08] ring-1 ring-emerald-500/25 flex items-center gap-2" data-testid="notify-back-success">
                              <CheckCheck className="w-3.5 h-3.5 text-emerald-300" />
                              <p className="text-emerald-300 text-[11px] font-semibold">You're on the list — we'll email {notifyEmail}</p>
                            </div>
                          )}

                          {/* Popular questions preview */}
                          {faqData && faqData.length > 0 && (
                            <div className="mb-3">
                              <p className="text-white/30 text-[10px] tracking-[0.18em] uppercase font-bold mb-2 flex items-center gap-1.5"><Star className="w-3 h-3" /> Popular right now</p>
                              <div className="space-y-1.5">
                                {faqData.slice(0, 3).map(f => (
                                  <button
                                    key={f.id}
                                    onClick={() => { setSelectedTopic(null); setInputMessage(f.question); handleStartChat(); }}
                                    data-testid={`welcome-hot-faq-${f.id}`}
                                    className="group w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/[0.02] ring-1 ring-white/[0.06] hover:ring-cyan-500/30 hover:bg-cyan-500/[0.04] transition-all text-left"
                                  >
                                    <HelpCircle className="w-3 h-3 text-cyan-400/70 flex-shrink-0" />
                                    <span className="flex-1 text-white/70 text-[11.5px] font-medium truncate group-hover:text-white transition-colors">{f.question}</span>
                                    <ChevronRight className="w-3 h-3 text-white/25 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Quick action row */}
                          <div className="mb-3 grid grid-cols-3 gap-1.5">
                            <button onClick={() => setWelcomeTab('chat')} className="group flex flex-col items-center gap-1 p-2.5 rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06] hover:ring-cyan-500/40 hover:bg-cyan-500/[0.05] transition-all" data-testid="quick-action-chat">
                              <MessageCircle className="w-4 h-4 text-cyan-300" />
                              <span className="text-white/70 text-[10px] font-semibold">Chat</span>
                            </button>
                            <a href="mailto:help@starnightholdings.uk" className="group flex flex-col items-center gap-1 p-2.5 rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06] hover:ring-violet-500/40 hover:bg-violet-500/[0.05] transition-all" data-testid="quick-action-email">
                              <Mail className="w-4 h-4 text-violet-300" />
                              <span className="text-white/70 text-[10px] font-semibold">Email</span>
                            </a>
                            <a href="https://discord.gg/2PFMA3hKjW" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center gap-1 p-2.5 rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06] hover:ring-indigo-500/40 hover:bg-indigo-500/[0.05] transition-all" data-testid="quick-action-discord">
                              <Users className="w-4 h-4 text-indigo-300" />
                              <span className="text-white/70 text-[10px] font-semibold">Discord</span>
                            </a>
                          </div>

                          {/* Primary CTA */}
                          <div className="mt-auto">
                            <button
                              onClick={handleStartChat}
                              data-testid="start-chat-btn"
                              className="relative w-full py-3 rounded-xl bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-semibold text-[13px] tracking-wide shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/45 transition-all flex items-center justify-center gap-2 overflow-hidden group"
                            >
                              <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100" style={{ animation: 'welcome-shine 2s linear infinite' }} />
                              <Sparkles className="w-4 h-4" />
                              Ask Nova anything
                            </button>
                            <p className="text-white/25 text-[10px] text-center mt-2">Powered by Nova AI · 24/7</p>
                          </div>
                        </div>
                      )}

                      {/* CHAT TAB — chat starters */}
                      {welcomeTab === 'chat' && (
                        <div className="flex-1 flex flex-col" data-testid="welcome-chat-panel">
                          <p className="text-white/30 text-[10px] tracking-[0.18em] uppercase font-bold mb-2.5 flex items-center gap-1.5"><MessageCircle className="w-3 h-3" /> Start a chat</p>
                          <div className="space-y-1.5 mb-3">
                            {CHAT_STARTERS.map((s) => (
                              <button
                                key={s.id}
                                onClick={() => startWith(s)}
                                data-testid={`chat-starter-${s.id}`}
                                className="group relative overflow-hidden w-full p-3 rounded-xl ring-1 ring-white/[0.06] bg-white/[0.02] hover:ring-white/15 hover:bg-white/[0.05] transition-all text-left"
                              >
                                <span aria-hidden className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-6 rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: s.color, boxShadow: `0 0 10px ${s.color}` }} />
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 transition-transform group-hover:scale-[1.06]" style={{ backgroundColor: `${s.color}18`, borderColor: `${s.color}40`, boxShadow: `0 4px 18px -6px ${s.color}66` }}>
                                    <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={2} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white/90 text-[12.5px] font-semibold leading-tight">{s.label}</p>
                                    <p className="text-white/40 text-[10.5px] mt-0.5 leading-tight">{s.sub}</p>
                                  </div>
                                  <ChevronRight className="w-3.5 h-3.5 text-white/25 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all" />
                                </div>
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={handleStartChat}
                            className="mt-auto w-full py-2.5 rounded-xl bg-slate-950/60 ring-1 ring-white/10 hover:ring-cyan-500/30 hover:bg-cyan-500/[0.06] text-white/70 hover:text-cyan-300 text-[12px] font-semibold transition-all flex items-center justify-center gap-1.5"
                          >
                            Or type your own question <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      {/* FAQ TAB — inline top FAQs */}
                      {welcomeTab === 'faq' && (
                        <div className="flex-1 flex flex-col" data-testid="welcome-faq-panel">
                          <p className="text-white/30 text-[10px] tracking-[0.18em] uppercase font-bold mb-2 flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> Frequently asked</p>
                          <div className="space-y-1.5 mb-3 max-h-[280px] overflow-y-auto no-scrollbar">
                            {(faqData || []).slice(0, 8).map(f => (
                              <FaqCard key={f.id} faq={f} onStartChat={() => { setInputMessage(f.question); handleStartChat(); }} />
                            ))}
                            {(!faqData || faqData.length === 0) && (
                              <p className="text-white/30 text-[11px] text-center py-6">Loading answers…</p>
                            )}
                          </div>
                          <button onClick={() => setShowFaq(true)}
                            className="mt-auto w-full py-2.5 rounded-xl bg-violet-500/[0.08] ring-1 ring-violet-500/25 hover:ring-violet-500/45 hover:bg-violet-500/[0.12] text-violet-300 text-[12px] font-semibold transition-all flex items-center justify-center gap-1.5"
                            data-testid="welcome-faq-see-all"
                          >
                            Browse all FAQs <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      {/* INFO TAB — hours, contact, socials */}
                      {welcomeTab === 'info' && (
                        <div className="flex-1 flex flex-col space-y-3" data-testid="welcome-info-panel">
                          <div className="rounded-xl p-3 bg-white/[0.02] ring-1 ring-white/[0.06]">
                            <p className="text-cyan-400/80 text-[10px] font-bold uppercase tracking-[0.14em] mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Live hours</p>
                            <p className="text-white text-[13px] font-semibold">10 AM – 10 PM · UK</p>
                            <p className="text-white/40 text-[10.5px]">Every day · Nova AI is always on</p>
                          </div>

                          <div className="rounded-xl p-3 bg-white/[0.02] ring-1 ring-white/[0.06]">
                            <p className="text-violet-400/80 text-[10px] font-bold uppercase tracking-[0.14em] mb-2 flex items-center gap-1"><Mail className="w-3 h-3" /> Reach us by email</p>
                            <div className="space-y-1.5">
                              <a href="mailto:help@starnightholdings.uk" className="flex items-center justify-between text-[11.5px] hover:text-cyan-300 text-white/70 transition-colors">
                                <span className="font-medium">General help</span>
                                <span className="text-cyan-400/80">help@</span>
                              </a>
                              <a href="mailto:people@starnightholdings.uk" className="flex items-center justify-between text-[11.5px] hover:text-cyan-300 text-white/70 transition-colors">
                                <span className="font-medium">Complaints / people</span>
                                <span className="text-cyan-400/80">people@</span>
                              </a>
                              <a href="mailto:finance@starnightholdings.uk" className="flex items-center justify-between text-[11.5px] hover:text-cyan-300 text-white/70 transition-colors">
                                <span className="font-medium">Billing / refunds</span>
                                <span className="text-cyan-400/80">finance@</span>
                              </a>
                            </div>
                          </div>

                          <div className="rounded-xl p-3 bg-white/[0.02] ring-1 ring-white/[0.06]">
                            <p className="text-indigo-400/80 text-[10px] font-bold uppercase tracking-[0.14em] mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Elsewhere</p>
                            <div className="grid grid-cols-2 gap-1.5">
                              <a href="https://discord.gg/2PFMA3hKjW" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-indigo-500/10 ring-1 ring-indigo-500/25 hover:ring-indigo-500/50 text-indigo-300 text-[11px] font-semibold transition-all">
                                <Users className="w-3 h-3" /> Discord
                              </a>
                              <a href="https://starnightholdings.uk" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/25 hover:ring-cyan-500/50 text-cyan-300 text-[11px] font-semibold transition-all">
                                <Globe className="w-3 h-3" /> Website
                              </a>
                            </div>
                          </div>

                          {onlineStaff.length > 0 && (
                            <div className="rounded-xl p-3 bg-white/[0.02] ring-1 ring-white/[0.06]">
                              <p className="text-emerald-400/80 text-[10px] font-bold uppercase tracking-[0.14em] mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> On the desk now ({onlineStaff.length})</p>
                              <div className="flex items-center -space-x-2">
                                {onlineStaff.slice(0, 6).map((s) => {
                                  const avatarUrl = s.avatar && s.avatar.startsWith('http') ? s.avatar : s.avatar ? `${BACKEND_URL}${s.avatar.startsWith('/api') ? s.avatar : '/api' + s.avatar}` : null;
                                  const failed = failedAvatars[`info-${s.username}`];
                                  return (
                                    <div key={s.username} className="relative group" title={`${s.display_name} · ${s.role}`}>
                                      <div className="w-8 h-8 rounded-full bg-slate-900 ring-2 ring-[#0A0A12] overflow-hidden flex items-center justify-center transition-transform group-hover:scale-110 group-hover:z-10">
                                        {avatarUrl && !failed ? (
                                          <img src={avatarUrl} alt={s.display_name} className="w-full h-full object-cover" onError={() => setFailedAvatars(prev => ({ ...prev, [`info-${s.username}`]: true }))} />
                                        ) : (
                                          <span className="text-[9px] font-bold text-cyan-300">{s.initials || '??'}</span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                {onlineStaff.length > 6 && (
                                  <div className="w-8 h-8 rounded-full bg-slate-800 ring-2 ring-[#0A0A12] flex items-center justify-center">
                                    <span className="text-[10px] font-bold text-white/60">+{onlineStaff.length - 6}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="relative flex-1 flex flex-col p-5 overflow-y-auto no-scrollbar" data-testid="chat-closed-screen">
                  {/* Compact team avatar strip — top */}
                  {onlineStaff.length > 0 && (
                    <div className="mb-4 flex items-center justify-center gap-2" data-testid="offline-team-row">
                      <div className="flex items-center -space-x-2">
                        {onlineStaff.slice(0, 4).map((s) => {
                          const avatarUrl = s.avatar && s.avatar.startsWith('http')
                            ? s.avatar
                            : s.avatar
                              ? `${BACKEND_URL}${s.avatar.startsWith('/api') ? s.avatar : '/api' + s.avatar}`
                              : null;
                          const failed = failedAvatars[`offline-${s.username}`];
                          return (
                            <div key={s.username} className="relative group" title={`${s.display_name} · ${s.role}`}>
                              <div className="w-7 h-7 rounded-full bg-slate-900 ring-2 ring-[#0A0A12] overflow-hidden flex items-center justify-center transition-transform group-hover:scale-110 group-hover:z-10">
                                {avatarUrl && !failed ? (
                                  <img src={avatarUrl} alt={s.display_name} className="w-full h-full object-cover"
                                    onError={() => setFailedAvatars(prev => ({ ...prev, [`offline-${s.username}`]: true }))} />
                                ) : (
                                  <span className="text-[9px] font-bold text-cyan-300">{s.initials || '??'}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-800/60 ring-1 ring-white/10">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                        <span className="text-white/60 text-[10px] font-semibold tracking-wide">{onlineStaff.length} team · away</span>
                      </div>
                    </div>
                  )}

                  {/* Simple moon header */}
                  <div className="text-center mb-5">
                    <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 ring-1 ring-white/[0.08] flex items-center justify-center">
                      <Moon className="w-6 h-6 text-cyan-300/70" strokeWidth={1.8} />
                    </div>
                    <h2 className="text-white text-lg font-bold tracking-tight mb-1">We're off-shift</h2>
                    <p className="text-white/45 text-[12px] leading-relaxed max-w-[280px] mx-auto">
                      {systemStatus.message || "Nova AI is on duty 24/7 — or leave your email and we'll ping you the second live chat reopens."}
                    </p>
                  </div>

                  {/* Primary options */}
                  <div className="space-y-2 mb-4">
                    <button onClick={() => { setShowFaq(false); handleStartChat(); }} data-testid="closed-chat-with-nova-btn"
                      className="group w-full p-3.5 rounded-xl ring-1 ring-cyan-500/25 bg-cyan-500/[0.06] hover:ring-cyan-500/45 hover:bg-cyan-500/[0.10] transition-all text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 p-[2px] flex-shrink-0">
                          <div className="w-full h-full rounded-[10px] bg-slate-950 flex items-center justify-center">
                            <Bot className="w-4 h-4 text-cyan-300" strokeWidth={2.2} />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-white text-[13px] font-semibold">Chat with Nova AI</p>
                            <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30">Online</span>
                          </div>
                          <p className="text-white/45 text-[11px] mt-0.5">Instant answers · no wait</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-cyan-400/60 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </button>

                    <button onClick={() => setShowFaq(true)} data-testid="closed-browse-faq-btn"
                      className="group w-full p-3.5 rounded-xl bg-white/[0.02] ring-1 ring-white/[0.06] hover:ring-violet-500/30 hover:bg-violet-500/[0.04] transition-all text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/15 ring-1 ring-violet-500/25 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-4 h-4 text-violet-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-[13px] font-semibold">Browse FAQs</p>
                          <p className="text-white/45 text-[11px] mt-0.5">Quick answers to common questions</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-violet-300 group-hover:translate-x-0.5 transition-all" />
                      </div>
                    </button>
                  </div>

                  {/* Notify-me — cyan restyle */}
                  <div className="mt-auto pt-2">
                    {notifyState === 'done' ? (
                      <div className="rounded-xl p-3.5 bg-emerald-500/[0.08] ring-1 ring-emerald-500/25" data-testid="notify-back-success">
                        <div className="flex items-start gap-2.5">
                          <CheckCheck className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                          <div className="min-w-0">
                            <p className="text-emerald-300 text-[12px] font-semibold">You're on the list</p>
                            <p className="text-white/50 text-[11px] mt-0.5 leading-relaxed">We'll email <span className="text-emerald-300/90 font-medium">{notifyEmail}</span> the moment live chat opens.</p>
                          </div>
                        </div>
                      </div>
                    ) : !showNotifyForm ? (
                      <button
                        onClick={() => setShowNotifyForm(true)}
                        data-testid="notify-back-toggle"
                        className="group w-full p-3 rounded-xl ring-1 ring-cyan-500/20 bg-cyan-500/[0.04] hover:ring-cyan-500/40 hover:bg-cyan-500/[0.08] transition-all text-left"
                      >
                        <div className="flex items-center gap-3">
                          <Bell className="w-4 h-4 text-cyan-300 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-[12px] font-semibold">Notify me when we're back</p>
                            <p className="text-white/40 text-[10.5px] mt-0.5">One-tap email ping · no spam</p>
                          </div>
                          <ChevronRight className="w-3.5 h-3.5 text-cyan-300/60 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </button>
                    ) : (
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (notifyState === 'loading') return;
                        setNotifyState('loading'); setNotifyError('');
                        try {
                          await axios.post(`${BACKEND_URL}/api/chat/notify-when-back`, { email: notifyEmail, message: notifyMessage, session_id: sessionId });
                          setNotifyState('done');
                        } catch (err) {
                          setNotifyError(err?.response?.data?.detail || 'Something went wrong');
                          setNotifyState('error');
                        }
                      }} className="rounded-xl p-3 ring-1 ring-cyan-500/25 bg-cyan-500/[0.04]" data-testid="notify-back-form">
                        <div className="flex items-center gap-2 mb-2">
                          <Bell className="w-3.5 h-3.5 text-cyan-300" />
                          <p className="text-cyan-300 text-[11px] font-bold uppercase tracking-[0.12em]">Ping me when live</p>
                          <button type="button" onClick={() => { setShowNotifyForm(false); setNotifyError(''); }} className="ml-auto text-white/30 hover:text-white/70 p-0.5" title="Close">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <input
                          type="email"
                          value={notifyEmail}
                          onChange={(e) => setNotifyEmail(e.target.value)}
                          placeholder="your@email.com"
                          autoFocus
                          required
                          data-testid="notify-back-email"
                          className="w-full bg-slate-950/60 ring-1 ring-white/[0.08] focus:ring-cyan-500/40 focus:bg-slate-950 rounded-lg px-3 py-2 text-white text-[12px] placeholder-white/25 focus:outline-none transition-all mb-2"
                        />
                        {notifyError && (
                          <p className="text-rose-300 text-[10.5px] font-medium mb-2 flex items-center gap-1">
                            <X className="w-3 h-3" /> {notifyError}
                          </p>
                        )}
                        <button
                          type="submit"
                          disabled={notifyState === 'loading' || !notifyEmail.trim()}
                          data-testid="notify-back-submit"
                          className="w-full py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-[12px] font-semibold shadow-lg shadow-cyan-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-1.5"
                        >
                          {notifyState === 'loading' ? (
                            <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Adding you…</>
                          ) : (
                            <><Bell className="w-3.5 h-3.5" /> Ping me back</>
                          )}
                        </button>
                      </form>
                    )}

                    <a href="mailto:help@starnightholdings.uk" className="mt-2.5 flex items-center gap-1.5 justify-center text-white/35 hover:text-cyan-300 text-[10.5px] font-medium tracking-wide transition-colors">
                      <Mail className="w-3 h-3" /> or email help@starnightholdings.uk
                    </a>
                    <p className="text-white/20 text-[10px] text-center mt-1.5 tracking-wide">
                      Live chat resumes <span className="text-cyan-300/70 font-semibold">10 AM UK</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* FAQ Screen — Vault */}
          {chatStatus === 'idle' && showFaq && (
            <div className="relative h-[536px] flex flex-col overflow-hidden" data-testid="faq-screen">
              <div aria-hidden className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-24 -left-12 w-72 h-72 rounded-full blur-3xl bg-violet-500/10" />
                <div className="absolute -bottom-24 -right-12 w-72 h-72 rounded-full blur-3xl bg-cyan-500/8" />
              </div>
              <div className="relative p-4 border-b border-white/[0.06]">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setShowFaq(false)} data-testid="faq-back-btn"
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-white/40 hover:text-cyan-300 hover:bg-cyan-500/10 text-[11px] font-medium transition-all">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500/25 to-fuchsia-500/15 ring-1 ring-violet-500/30 flex items-center justify-center">
                      <BookOpen className="w-3 h-3 text-violet-300" />
                    </div>
                    <h3 className="text-white text-[13px] font-bold tracking-tight">FAQs</h3>
                  </div>
                  <div className="w-14" />
                </div>
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
                  <input type="text" value={faqSearch} onChange={e => setFaqSearch(e.target.value)}
                    placeholder="Search common questions…" data-testid="faq-search"
                    className="w-full bg-slate-950/60 ring-1 ring-white/[0.08] hover:ring-white/15 focus:ring-violet-500/40 focus:bg-slate-950 rounded-xl pl-10 pr-4 py-2.5 text-white text-[12px] placeholder-white/25 focus:outline-none transition-all" />
                  {faqSearch && (
                    <button onClick={() => setFaqSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-white/30 hover:text-white/70 hover:bg-white/5 transition-all">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {faqSearch && (
                  <p className="mt-2 text-[10px] text-white/30 tracking-wide">{filteredFaq.length} result{filteredFaq.length !== 1 ? 's' : ''}</p>
                )}
              </div>
              <div className="relative flex-1 overflow-y-auto no-scrollbar p-4 space-y-2">
                {filteredFaq.map(faq => (
                  <FaqCard key={faq.id} faq={faq} onStartChat={(q) => {
                    setShowFaq(false);
                    setSelectedTopic(null);
                    handleStartChat();
                  }} />
                ))}
                {filteredFaq.length === 0 && (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 rounded-xl bg-white/[0.03] ring-1 ring-white/[0.06] flex items-center justify-center mx-auto mb-3">
                      <Search className="w-5 h-5 text-white/25" />
                    </div>
                    <p className="text-white/30 text-[12px] font-medium mb-1">No matches found</p>
                    <p className="text-white/20 text-[11px] mb-3">Try a different keyword — or ask Nova directly.</p>
                    <button onClick={() => { setShowFaq(false); handleStartChat(); }} data-testid="faq-ask-nova-btn"
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-cyan-500/10 ring-1 ring-cyan-500/30 text-cyan-300 text-[11px] font-semibold hover:bg-cyan-500/20 transition-all">
                      <Bot className="w-3 h-3" /> Ask Nova instead
                    </button>
                  </div>
                )}
              </div>
              <div className="relative p-3 border-t border-white/[0.06]">
                <button onClick={() => { setShowFaq(false); handleStartChat(); }} data-testid="faq-chat-btn"
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white py-2.5 text-[12px] font-semibold shadow-lg shadow-violet-500/25 hover:shadow-violet-500/45 transition-all flex items-center justify-center gap-2">
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100" style={{ animation: 'welcome-shine 2s linear infinite' }} />
                  <MessageCircle className="relative w-3.5 h-3.5" />
                  <span className="relative">Can't find it? Chat with Nova</span>
                </button>
              </div>
            </div>
          )}

          {/* User Info Form */}
          {chatStatus === 'intro' && (
            <div className="h-[536px] flex flex-col p-6" data-testid="chat-intro-form">
              <button onClick={() => { setChatStatus('idle'); setSelectedTopic(null); }}
                className="flex items-center gap-1.5 text-white/30 hover:text-white/60 text-xs mb-4 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <div className="mb-6">
                <h2 className="text-white text-lg font-semibold tracking-tight mb-1">Quick intro</h2>
                <p className="text-white/30 text-sm">So we know who we're chatting with</p>
              </div>
              <div className="space-y-4 flex-1">
                <div>
                  <label className="flex items-center text-white/40 text-xs mb-2 tracking-wide uppercase font-medium">
                    <User className="w-3.5 h-3.5 mr-2 text-[#00E5FF]" /> Name
                  </label>
                  <input type="text" value={userInfo.name}
                    onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                    onKeyPress={handleKeyPress} placeholder="What should we call you?" autoFocus
                    data-testid="chat-name-input"
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/50 focus:border-[#00E5FF]/30 transition-all text-sm" />
                </div>
                <div>
                  <label className="flex items-center text-white/40 text-xs mb-2 tracking-wide uppercase font-medium">
                    <Mail className="w-3.5 h-3.5 mr-2 text-[#00E5FF]" /> Email
                  </label>
                  <input type="email" value={userInfo.email}
                    onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })}
                    onKeyPress={handleKeyPress} placeholder="your@email.com"
                    data-testid="chat-email-input"
                    className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/50 focus:border-[#00E5FF]/30 transition-all text-sm" />
                </div>
                {selectedTopic && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                    <Zap className="w-3.5 h-3.5 text-[#00E5FF]" />
                    <span className="text-white/40 text-xs">Topic: <span className="text-white/60">{QUICK_TOPICS.find(t => t.id === selectedTopic)?.label}</span></span>
                  </div>
                )}
              </div>
              <Button onClick={handleSubmitUserInfo}
                disabled={!userInfo.name.trim() || !userInfo.email.trim()}
                data-testid="submit-user-info-btn"
                className="w-full bg-gradient-to-r from-[#00E5FF] to-[#0099CC] hover:opacity-90 disabled:opacity-30 text-white py-3 rounded-xl font-medium shadow-lg transition-all">
                Start Chatting <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-white/15 text-[10px] text-center mt-3">Your info helps us serve you better</p>
            </div>
          )}

          {/* Chat Interface */}
          {(chatStatus === 'active' || chatStatus === 'connected') && !showRating && (
            <>
              {/* Status bars */}
              {humanRequested && !isConnectedToHuman && (
                <div className="bg-amber-500/5 border-b border-amber-500/10 px-4 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-amber-400/80 text-[11px] font-medium flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
                      Waiting for a support agent…
                    </p>
                    {waitEstimate && (
                      <p className="text-amber-300/60 text-[10px] tabular-nums flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />
                        {waitEstimate.queue_position <= 1
                          ? '~1 min'
                          : `#${waitEstimate.queue_position} in queue · ~${waitEstimate.estimated_wait_minutes}m`}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {isConnectedToHuman && (
                <div className="bg-emerald-500/5 border-b border-emerald-500/10 px-4 py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {assignedStaff?.avatar && !failedAvatars[`assigned-${assignedStaff.username}`] ? (
                      <img src={assignedStaff.avatar} alt={assignedStaff.display_name}
                        className="w-6 h-6 rounded-full object-cover border border-emerald-500/30 flex-shrink-0"
                        onError={() => setFailedAvatars(prev => ({ ...prev, [`assigned-${assignedStaff.username}`]: true }))} />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-bold text-emerald-300">{(assignedStaff?.display_name || 'S').charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-emerald-300/90 text-[11px] font-medium flex items-center gap-1.5 truncate">
                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full flex-shrink-0" />
                        {assignedStaff ? `${assignedStaff.display_name} is helping you` : 'Connected to support agent'}
                      </p>
                      {assignedStaff?.role && (
                        <p className="text-emerald-300/40 text-[9px] tracking-[0.08em] uppercase truncate">{assignedStaff.role}</p>
                      )}
                    </div>
                  </div>
                  <button onClick={handleEndChat} data-testid="end-chat-btn"
                    className="text-white/20 hover:text-white/50 text-[10px] transition-colors flex-shrink-0">End chat</button>
                </div>
              )}

              {/* Messages */}
              <div
                ref={messagesScrollRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
                  setShowScrollBottom(!nearBottom && messages.length > 3);
                }}
                className={`relative overflow-y-auto no-scrollbar p-4 space-y-3 ${
                showEmailTranscript ? 'h-[348px]' : humanRequested || isConnectedToHuman ? 'h-[392px]' : 'h-[424px]'
              }`}>
                {messages.map((msg, index) => {
                  const parsed = msg.sender_type === 'bot' ? parseQuickReplies(msg.message) : { text: msg.message, replies: [] };
                  return (
                    <div key={msg.message_id || index}>
                      <div className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                        style={{ animation: 'fadeInUp 0.2s ease-out' }}>
                        {msg.sender_type !== 'user' && (
                          <div className="flex-shrink-0 mr-2 self-end">
                            <div className={`relative w-8 h-8 rounded-xl p-[1.5px] shadow-lg ${
                              msg.sender_type === 'staff'
                                ? 'bg-gradient-to-br from-emerald-400 to-teal-600 shadow-emerald-500/25'
                                : 'bg-gradient-to-br from-cyan-400 to-blue-600 shadow-cyan-500/25'
                            }`}>
                              <div className="w-full h-full rounded-[9px] bg-slate-950 flex items-center justify-center overflow-hidden">
                                {msg.sender_type === 'staff' && msg.sender_avatar && !failedAvatars[msg.sender_avatar] ? (
                                  <img src={msg.sender_avatar} alt={msg.sender_name}
                                    className="w-full h-full object-cover"
                                    onError={() => setFailedAvatars(prev => ({ ...prev, [msg.sender_avatar]: true }))} />
                                ) : msg.sender_type === 'staff' ? (
                                  <span className="text-[10px] font-bold text-emerald-300">{msg.sender_name?.charAt(0)?.toUpperCase() || 'S'}</span>
                                ) : (
                                  <Bot className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.2} />
                                )}
                              </div>
                              {/* Tiny online dot on avatar */}
                              <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-[1.5px] ring-slate-950 ${
                                msg.sender_type === 'staff' ? 'bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.9)]' : 'bg-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.9)]'
                              }`} />
                            </div>
                          </div>
                        )}
                        <div className="max-w-[80%]">
                          {msg.sender_type !== 'user' && (
                            <p className={`text-[10px] mb-1 ml-1 font-semibold tracking-wide ${
                              msg.sender_type === 'staff' ? 'text-emerald-300/60' : 'text-cyan-300/60'
                            }`}>{msg.sender_name}</p>
                          )}
                          <div className={`relative overflow-hidden rounded-2xl px-4 py-2.5 ${
                            msg.deleted
                              ? 'bg-white/[0.02] text-white/30 ring-1 ring-white/[0.04] rounded-bl-md'
                              : msg.sender_type === 'user'
                                ? 'bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 text-white rounded-br-md shadow-lg shadow-cyan-500/25 ring-1 ring-cyan-400/40'
                                : msg.sender_type === 'staff'
                                ? 'bg-white/[0.04] text-white/85 ring-1 ring-emerald-500/15 rounded-bl-md'
                                : 'bg-white/[0.04] text-white/85 ring-1 ring-cyan-500/15 rounded-bl-md'
                          }`}>
                            {/* Subtle shine sweep on user bubble */}
                            {!msg.deleted && msg.sender_type === 'user' && (
                              <span
                                aria-hidden
                                className="pointer-events-none absolute inset-0 opacity-40"
                                style={{
                                  background: 'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)',
                                  backgroundSize: '250% 100%',
                                  animation: 'bubble-shine 3.5s ease-in-out infinite',
                                }}
                              />
                            )}
                            {msg.deleted ? (
                              <p className="relative text-[13px] italic">This message was deleted</p>
                            ) : (
                              <p className="relative text-[13px] whitespace-pre-wrap break-words leading-relaxed">{parsed.text}</p>
                            )}
                          </div>
                          <div className={`flex items-center gap-1.5 mt-1 ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start ml-1'}`}>
                            <span className="text-[10px] text-white/15">{getTimeAgo(msg.timestamp)}</span>
                            {msg.edited && !msg.deleted && <span className="text-[10px] text-white/20">(edited)</span>}
                            {msg.sender_type === 'user' && <CheckCheck className="w-3 h-3 text-[#00E5FF]/40" />}
                            {/* Reaction buttons for bot & staff messages */}
                            {(msg.sender_type === 'bot' || msg.sender_type === 'staff') && !msg.deleted && msg.message_id && (
                              <div className="flex items-center gap-0.5 ml-1 group/reactions">
                                <button
                                  onClick={() => handleReaction(msg.message_id, 'heart')}
                                  data-testid={`reaction-heart-${msg.message_id}`}
                                  title="Love this"
                                  className={`p-1 rounded-md ring-1 transition-all ${
                                    reactions[msg.message_id] === 'heart'
                                      ? 'text-rose-300 bg-rose-500/15 ring-rose-500/35 shadow-[0_0_8px_rgba(244,63,94,0.35)] scale-110'
                                      : 'text-white/20 ring-transparent hover:text-rose-300 hover:bg-rose-500/10 hover:ring-rose-500/25'
                                  }`}
                                >
                                  <Heart className={`w-3 h-3 transition-all ${reactions[msg.message_id] === 'heart' ? 'fill-rose-400' : ''}`} />
                                </button>
                                <button
                                  onClick={() => handleReaction(msg.message_id, 'thumbs_up')}
                                  data-testid={`reaction-up-${msg.message_id}`}
                                  title="Helpful"
                                  className={`p-1 rounded-md ring-1 transition-all ${
                                    reactions[msg.message_id] === 'thumbs_up'
                                      ? 'text-emerald-300 bg-emerald-500/15 ring-emerald-500/35 shadow-[0_0_8px_rgba(52,211,153,0.35)] scale-110'
                                      : 'text-white/20 ring-transparent hover:text-emerald-300 hover:bg-emerald-500/10 hover:ring-emerald-500/25'
                                  }`}
                                >
                                  <ThumbsUp className="w-3 h-3" />
                                </button>
                                {msg.sender_type === 'bot' && (
                                  <button
                                    onClick={() => handleReaction(msg.message_id, 'thumbs_down')}
                                    data-testid={`reaction-down-${msg.message_id}`}
                                    title="Not helpful"
                                    className={`p-1 rounded-md ring-1 transition-all ${
                                      reactions[msg.message_id] === 'thumbs_down'
                                        ? 'text-amber-300 bg-amber-500/15 ring-amber-500/35 shadow-[0_0_8px_rgba(245,158,11,0.35)] scale-110'
                                        : 'text-white/20 ring-transparent hover:text-amber-300 hover:bg-amber-500/10 hover:ring-amber-500/25'
                                    }`}
                                  >
                                    <ThumbsDown className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Quick Reply Buttons */}
                      {parsed.replies.length > 0 && index === messages.length - 1 && (
                        <div className="flex flex-wrap gap-1.5 mt-2 ml-9" style={{ animation: 'fadeInUp 0.3s ease-out 0.1s both' }}>
                          {parsed.replies.map(reply => (
                            <button key={reply} onClick={() => handleQuickReply(reply)}
                              data-testid={`quick-reply-${reply.toLowerCase().replace(/\s/g, '-')}`}
                              className="px-3 py-1.5 rounded-full border border-[#00E5FF]/20 bg-[#00E5FF]/5 text-[#00E5FF] text-[11px] font-medium hover:bg-[#00E5FF]/15 hover:border-[#00E5FF]/40 transition-all">
                              {reply}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {isLoading && (
                  <div className="flex justify-start" style={{ animation: 'fadeInUp 0.2s ease-out' }}>
                    <div className="flex-shrink-0 mr-2 self-end">
                      <div className="w-7 h-7 rounded-lg bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
                        <Bot className="w-3.5 h-3.5 text-[#00E5FF]" />
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/20 mb-1 ml-1 font-medium">Nova</p>
                      <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3">
                        <p className="text-[10px] text-[#00E5FF]/50 mb-1.5">Nova is thinking...</p>
                        <div className="flex gap-1.5">
                          <div className="w-2 h-2 bg-[#00E5FF]/50 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-[#00E5FF]/50 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                          <div className="w-2 h-2 bg-[#00E5FF]/50 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {staffTyping && !isLoading && (
                  <div className="flex justify-start" style={{ animation: 'fadeInUp 0.2s ease-out' }}>
                    <div className="flex-shrink-0 mr-2 self-end">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-emerald-400">{staffTypingName?.charAt(0) || 'S'}</span>
                      </div>
                    </div>
                    <div className="bg-white/[0.04] border border-emerald-500/10 rounded-2xl rounded-bl-md px-4 py-3">
                      <p className="text-[10px] text-emerald-400/50 mb-1">{staffTypingName} is typing</p>
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-emerald-400/50 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-emerald-400/50 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }} />
                        <div className="w-1.5 h-1.5 bg-emerald-400/50 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="px-4 py-3 border-t border-white/[0.04] bg-gradient-to-b from-white/[0.02] to-transparent" data-testid="emoji-picker" style={{ animation: 'fadeInUp 0.15s ease-out' }}>
                  <p className="text-white/25 text-[9px] tracking-[0.15em] uppercase font-bold mb-2">React with</p>
                  <div className="flex flex-wrap gap-1">
                    {EMOJI_LIST.map(emoji => (
                      <button key={emoji} onClick={() => { setInputMessage(prev => prev + emoji); setShowEmojiPicker(false); inputRef.current?.focus(); }}
                        className="w-9 h-9 rounded-xl hover:bg-cyan-500/10 hover:scale-110 ring-1 ring-transparent hover:ring-cyan-500/25 flex items-center justify-center text-lg transition-all">
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Scroll-to-bottom floating pill */}
              {showScrollBottom && (
                <button
                  onClick={() => { messagesScrollRef.current?.scrollTo({ top: messagesScrollRef.current.scrollHeight, behavior: 'smooth' }); setShowScrollBottom(false); }}
                  data-testid="scroll-to-bottom-btn"
                  className="absolute right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-950/95 ring-1 ring-cyan-500/40 text-cyan-300 text-[10.5px] font-semibold shadow-xl shadow-cyan-500/20 backdrop-blur-md hover:bg-cyan-500/15 transition-all"
                  style={{ bottom: '90px', animation: 'fadeInUp 0.2s ease-out' }}
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                  Jump to latest
                </button>
              )}

              {/* Input — Vault composer */}
              <div className="relative p-3 border-t border-white/[0.06] bg-gradient-to-b from-transparent to-white/[0.02]">
                <div className="relative flex items-end gap-1.5">
                  {/* Emoji + attach rail */}
                  <div className="flex items-center gap-0.5 pb-[3px]">
                    <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} data-testid="emoji-toggle-btn"
                      title="Add emoji"
                      className={`p-2 rounded-lg transition-all flex-shrink-0 ${showEmojiPicker ? 'text-cyan-300 bg-cyan-500/15 ring-1 ring-cyan-500/30' : 'text-white/30 hover:text-cyan-300 hover:bg-cyan-500/10'}`}>
                      <SmilePlus className="w-4 h-4" />
                    </button>
                    <label
                      title="Attach image or document (max 8MB)"
                      data-testid="attach-file-btn"
                      className={`p-2 rounded-lg transition-all flex-shrink-0 cursor-pointer ${uploadingFile ? 'text-cyan-300 bg-cyan-500/15 animate-pulse' : 'text-white/30 hover:text-cyan-300 hover:bg-cyan-500/10'}`}>
                      <Paperclip className="w-4 h-4" />
                      <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile}
                        accept="image/*,.pdf,.doc,.docx,.txt" data-testid="attach-file-input" />
                    </label>
                  </div>

                  {/* Text input with soft ring */}
                  <div className="relative flex-1">
                    <input ref={inputRef} type="text" value={inputMessage}
                      onChange={(e) => { handleCustomerInputChange(e); setCharCount(e.target.value.length); }}
                      onKeyPress={handleKeyPress}
                      maxLength={2000}
                      placeholder={isConnectedToHuman ? `Message ${assignedStaff?.display_name || 'the team'}…` : 'Type a message…'}
                      data-testid="chat-message-input"
                      className="w-full bg-slate-950/60 ring-1 ring-white/[0.08] hover:ring-white/15 focus:ring-cyan-500/40 focus:bg-slate-950 rounded-2xl px-4 py-2.5 pr-16 text-white placeholder-white/25 focus:outline-none text-[13px] transition-all"
                      disabled={isLoading} />
                    {charCount > 1600 && (
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[9px] tabular-nums font-mono ${charCount >= 2000 ? 'text-rose-400' : charCount >= 1800 ? 'text-amber-400' : 'text-white/30'}`}>
                        {charCount}/2000
                      </span>
                    )}
                  </div>

                  {/* Send button — shine-sweep */}
                  <button
                    onClick={() => { handleSendMessage(); setCharCount(0); }}
                    disabled={!inputMessage.trim() || isLoading}
                    data-testid="send-message-btn"
                    className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white h-10 w-10 flex items-center justify-center shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:shadow-cyan-500/30 flex-shrink-0"
                  >
                    <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100" style={{ animation: 'welcome-shine 2s linear infinite' }} />
                    <Send className="relative w-4 h-4" strokeWidth={2.4} />
                  </button>
                </div>

                {/* Footer strip — hints */}
                <div className="flex items-center justify-between mt-2 px-1">
                  <p className="text-white/25 text-[9.5px] flex items-center gap-1 tracking-wide">
                    <span className="hidden sm:inline">Press</span>
                    <kbd className="px-1 py-[1px] rounded bg-white/[0.06] ring-1 ring-white/10 text-[8.5px] font-mono text-white/50">Enter</kbd>
                    <span>to send</span>
                  </p>
                  <p className="text-white/20 text-[9.5px] flex items-center gap-1 tracking-wide">
                    <Sparkles className="w-2.5 h-2.5 text-cyan-400/60" />
                    Powered by <span className="text-cyan-400/70 font-semibold">Nova AI</span>
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Rating Screen — Vault */}
          {showRating && (
            <div className="relative h-[536px] flex flex-col items-center justify-center p-6 text-center overflow-hidden" data-testid="chat-rating-screen">
              <div aria-hidden className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-16 -left-8 w-64 h-64 rounded-full blur-3xl bg-amber-500/10" />
                <div className="absolute -bottom-16 -right-8 w-64 h-64 rounded-full blur-3xl bg-rose-500/10" />
              </div>
              {!ratingSubmitted ? (
                <div className="relative w-full max-w-sm">
                  <div className="relative w-16 h-16 mx-auto mb-4">
                    <div aria-hidden className="absolute -inset-1 rounded-2xl opacity-70 blur-[2px]" style={{ background: 'conic-gradient(from 0deg, transparent 0deg, #F59E0B 90deg, transparent 180deg, #F43F5E 270deg, transparent 360deg)', animation: 'welcome-spin 6s linear infinite', maskImage: 'radial-gradient(closest-side, transparent 68%, black 70%, black 100%)', WebkitMaskImage: 'radial-gradient(closest-side, transparent 68%, black 70%, black 100%)' }} />
                    <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-amber-400 to-rose-500 p-[2px] shadow-xl shadow-amber-500/30">
                      <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center">
                        <Heart className="w-7 h-7 text-amber-300" strokeWidth={2.2} fill="currentColor" />
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-amber-400/80 mb-1.5">Your voice matters</p>
                  <h2 className="text-white text-xl font-bold tracking-tight mb-1">How was your experience?</h2>
                  <p className="text-white/45 text-[12px] mb-5">Tap the stars — your feedback shapes what we build next.</p>

                  <div className="flex justify-center gap-2 mb-4">
                    {[1, 2, 3, 4, 5].map(i => {
                      const active = i <= rating;
                      return (
                        <button
                          key={i}
                          onClick={() => setRating(i)}
                          onMouseEnter={() => setRating(prev => prev === 0 ? 0 : prev)}
                          data-testid={`rating-star-${i}`}
                          className={`group relative w-11 h-11 rounded-xl ring-1 transition-all duration-200 flex items-center justify-center ${
                            active
                              ? 'bg-amber-400/20 ring-amber-400/50 scale-110 shadow-lg shadow-amber-500/30'
                              : 'bg-white/[0.03] ring-white/[0.08] hover:bg-white/[0.06] hover:ring-white/15 hover:scale-105'
                          }`}
                        >
                          <Star className={`w-5 h-5 transition-all ${active ? 'text-amber-400 fill-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]' : 'text-white/25 group-hover:text-white/50'}`} />
                        </button>
                      );
                    })}
                  </div>
                  {rating > 0 && (
                    <p className="text-[11px] text-amber-300/80 font-semibold mb-3">
                      {rating === 5 ? '💎 Amazing — thanks!' : rating === 4 ? '✨ Great to hear' : rating === 3 ? '👍 Good to know' : rating === 2 ? '👀 We can do better' : '🙏 We hear you'}
                    </p>
                  )}
                  {rating > 0 && (
                    <textarea
                      value={ratingComment}
                      onChange={(e) => setRatingComment(e.target.value.slice(0, 500))}
                      placeholder={rating >= 4 ? "What did we do well? (optional)" : "How can we improve? (optional)"}
                      rows={3}
                      maxLength={500}
                      data-testid="rating-comment-input"
                      className="w-full bg-slate-950/60 ring-1 ring-white/[0.08] focus:ring-amber-500/40 focus:bg-slate-950 rounded-xl px-3.5 py-2.5 text-white/85 placeholder-white/25 focus:outline-none text-[13px] resize-none mb-4 transition-all"
                      style={{ animation: 'fadeInUp 0.25s ease-out' }}
                    />
                  )}
                  <div className="flex gap-2 w-full">
                    <button
                      onClick={handleNewChat}
                      className="flex-1 py-2.5 rounded-xl ring-1 ring-white/10 text-white/50 hover:text-white/80 hover:ring-white/20 hover:bg-white/[0.03] text-[12px] font-semibold transition-all"
                    >
                      Skip
                    </button>
                    <button
                      onClick={handleSubmitRating}
                      disabled={rating === 0}
                      data-testid="submit-rating-btn"
                      className="group relative flex-1 py-2.5 rounded-xl overflow-hidden bg-gradient-to-r from-amber-500 via-rose-500 to-amber-500 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white text-[12px] font-semibold shadow-lg shadow-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100" style={{ animation: 'welcome-shine 2s linear infinite' }} />
                      <span className="relative">Submit feedback</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative w-16 h-16 mx-auto mb-4">
                    <div aria-hidden className="absolute -inset-1 rounded-2xl opacity-70 blur-[2px]" style={{ background: 'conic-gradient(from 0deg, transparent 0deg, #10B981 90deg, transparent 180deg, #00E5FF 270deg, transparent 360deg)', animation: 'welcome-spin 6s linear infinite', maskImage: 'radial-gradient(closest-side, transparent 68%, black 70%, black 100%)', WebkitMaskImage: 'radial-gradient(closest-side, transparent 68%, black 70%, black 100%)' }} />
                    <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 p-[2px] shadow-xl shadow-emerald-500/30">
                      <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center">
                        <CheckCheck className="w-7 h-7 text-emerald-300" strokeWidth={2.2} />
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-400/80 mb-1.5">Feedback received</p>
                  <h2 className="text-white text-xl font-bold tracking-tight mb-1">Thank you!</h2>
                  <p className="text-white/45 text-[12px]">Starting a fresh conversation…</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bubble-shine {
          0%, 100% { background-position: 200% 0; opacity: 0.35; }
          50% { background-position: -200% 0; opacity: 0.55; }
        }
      `}</style>
    </div>
  );
};

// FAQ Card Component — Vault polish
const FaqCard = ({ faq, onStartChat }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`group relative overflow-hidden rounded-xl ring-1 transition-all ${expanded ? 'ring-violet-500/30 bg-violet-500/[0.04]' : 'ring-white/[0.06] bg-white/[0.02] hover:ring-white/15 hover:bg-white/[0.04]'}`}>
      {expanded && (
        <span aria-hidden className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-violet-400 via-fuchsia-500 to-violet-400 shadow-[0_0_8px_rgba(139,92,246,0.7)]" />
      )}
      <button onClick={() => setExpanded(!expanded)}
        data-testid={`faq-item-${faq.id}`}
        className="w-full p-3 flex items-center justify-between text-left gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ring-1 transition-all ${expanded ? 'bg-violet-500/20 ring-violet-500/40' : 'bg-white/[0.04] ring-white/[0.06] group-hover:ring-white/15'}`}>
            <HelpCircle className={`w-3.5 h-3.5 transition-colors ${expanded ? 'text-violet-300' : 'text-white/40 group-hover:text-white/60'}`} />
          </div>
          <span className={`text-[12px] font-semibold transition-colors ${expanded ? 'text-white' : 'text-white/75 group-hover:text-white/90'}`}>{faq.question}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-all ${expanded ? 'rotate-180 text-violet-300' : 'text-white/25 group-hover:text-white/50'}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 pl-[52px]" style={{ animation: 'fadeInUp 0.15s ease-out' }}>
          <p className="text-white/60 text-[12px] leading-relaxed">{faq.answer}</p>
          <button
            onClick={(e) => { e.stopPropagation(); onStartChat && onStartChat(faq.question); }}
            className="mt-2.5 inline-flex items-center gap-1 text-[10.5px] font-semibold text-violet-300 hover:text-violet-200 tracking-wide transition-colors"
          >
            Ask a follow-up
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
